import { Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import { PenTool, BookOpen, ChevronRight } from 'lucide-react'
import { Card, CardContent } from '@/components/ui'
import { useFamily } from '@/context/FamilyContext'

export function StudioLanding() {
  const { activeWriter } = useFamily()

  return (
    <div className="max-w-4xl mx-auto p-6">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="text-center mb-10"
      >
        <h1 className="font-display text-[var(--font-size-kid-2xl)] font-bold text-[var(--color-kids-text)] mb-2">
          Writing Studio
        </h1>
        <p className="text-[var(--color-kids-text-muted)] text-lg">
          Choose your path, {activeWriter?.display_name}!
        </p>
      </motion.div>

      {/* Mode selection */}
      <div className="grid md:grid-cols-2 gap-6">
        {/* Free Writing */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
        >
          <Link to="/studio/write">
            <Card className="h-full hover:shadow-lg transition-all hover:scale-[1.02] cursor-pointer group bg-gradient-to-br from-[var(--color-kids-primary)] to-[var(--color-kids-primary-dark)] text-white">
              <CardContent className="p-8">
                <div className="w-16 h-16 rounded-full bg-white/20 flex items-center justify-center mb-6 group-hover:bg-white/30 transition-colors">
                  <PenTool className="w-8 h-8" />
                </div>
                <h2 className="font-display text-2xl font-bold mb-3">
                  Free Writing
                </h2>
                <p className="opacity-90 mb-6 leading-relaxed">
                  Write your own stories with helpful organization tools.
                  Create chapters, add notes, and export your work when you're done!
                </p>
                <div className="flex items-center gap-2 font-medium">
                  Start Writing
                  <ChevronRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </div>
              </CardContent>
            </Card>
          </Link>
        </motion.div>

        {/* Learning Path */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          <Link to="/studio/learn">
            <Card className="h-full hover:shadow-lg transition-all hover:scale-[1.02] cursor-pointer group border-2 border-[var(--color-kids-secondary)]/20">
              <CardContent className="p-8">
                <div className="w-16 h-16 rounded-full bg-[var(--color-kids-secondary)]/10 flex items-center justify-center mb-6 group-hover:bg-[var(--color-kids-secondary)]/20 transition-colors">
                  <BookOpen className="w-8 h-8 text-[var(--color-kids-secondary)]" />
                </div>
                <h2 className="font-display text-2xl font-bold text-[var(--color-kids-text)] mb-3">
                  Learning Path
                </h2>
                <p className="text-[var(--color-kids-text-muted)] mb-6 leading-relaxed">
                  Follow guided lessons to improve your writing skills.
                  Learn new techniques with fun prompts and examples!
                </p>
                <div className="flex items-center gap-2 font-medium text-[var(--color-kids-secondary)]">
                  Explore Lessons
                  <ChevronRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </div>
              </CardContent>
            </Card>
          </Link>
        </motion.div>
      </div>

      {/* Info footer */}
      <motion.p
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.4 }}
        className="text-center text-sm text-[var(--color-kids-text-muted)] mt-8"
      >
        Your writing is saved automatically and stays on this device.
      </motion.p>
    </div>
  )
}
